import React from 'react';
import { ResponsiveECharts } from './ResponsiveECharts';
import { ChartCard } from '../ChartCard';
import '../studysyncEChartsTheme'; // side effect: registers the 'studysync' theme before render
import { tokens } from '../tokens';
import { ECHARTS_ANIM, seriesLead } from '../motion';
import { TIMING } from '../animationConfig';
import type { ChartSpec, ChartData } from '../copilotTypes';

type TrendLineSpec = Extract<ChartSpec, { chart: 'trend_line' }>;
type TrendLineData = Extract<ChartData, { chart: 'trend_line' }>;

const SERIES_NAME = 'Daily';
const ROLLING_NAME = 'Rolling avg';
const TREND_NAME = 'Trend';

export function buildTrendLineOption(_spec: TrendLineSpec, data: TrendLineData) {
  const { points, rollingAvg, trend, unit } = data;

  // The x-axis is the union of every date that appears in any series, sorted ascending, so the
  // daily line and the (possibly shorter/offset) rolling-average line share one category axis and
  // align point-for-point. Looking a value up by date avoids index drift between the two series.
  const dates = Array.from(
    new Set([...points.map((p) => p.date), ...(rollingAvg ?? []).map((p) => p.date)]),
  ).sort();

  const valueByDate = (series: Array<{ date: string; value: number }>) => {
    const map = new Map(series.map((p) => [p.date, p.value]));
    // Gaps stay as null so the line connects across missing days without dropping to zero.
    return dates.map((d) => (map.has(d) ? map.get(d)! : null));
  };

  // Layers build outline-first (see seriesLead): the daily line (the data's outline) draws after a beat
  // that lets the empty framed grid register, then the rolling average and the trend overlay each join
  // one lead behind — so the chart reads as a calm sequence of settling layers, not a simultaneous rush.
  const series: any[] = [
    {
      name: SERIES_NAME,
      type: 'line',
      data: valueByDate(points),
      showSymbol: false,
      connectNulls: true,
      lineStyle: { width: 2 },
      // Series color comes from the registered theme palette (index 0).
      animationDelay: seriesLead(0),
    },
  ];

  if (rollingAvg && rollingAvg.length) {
    series.push({
      name: ROLLING_NAME,
      type: 'line',
      data: valueByDate(rollingAvg),
      showSymbol: false,
      connectNulls: true,
      smooth: true,
      // Lighter, thicker stroke reads as a calm "smoothed" line sitting under the busier daily line.
      lineStyle: { width: 3, opacity: 0.55 },
      // Joins after the daily line has substantially drawn.
      animationDelay: seriesLead(1),
    });
  }

  if (trend) {
    // The trend is a straight regression line; ECharts draws a line by connecting points, so we
    // only need its two endpoints across the date range. The fit is value = intercept + slope * i,
    // where i is the zero-based position of a point. We anchor it to the first and last categories
    // (firstX, intercept) -> (lastX, intercept + slope * (n - 1)) so it spans the whole x-axis.
    const n = points.length;
    const firstDate = dates[0];
    const lastDate = dates[dates.length - 1];
    const startValue = trend.intercept;
    const endValue = trend.intercept + trend.slope * Math.max(0, n - 1);
    series.push({
      name: TREND_NAME,
      type: 'line',
      data: [
        [firstDate, startValue],
        [lastDate, endValue],
      ],
      showSymbol: false,
      // Drawn against the category axis by date so it lines up with the other series' endpoints.
      lineStyle: { color: tokens.brand.cardinal, type: 'dashed', width: 2 },
      itemStyle: { color: tokens.brand.cardinal },
      // The trend is the last layer: it draws THROUGH the settled lines once they've landed (a quicker
      // sweep than the data draw), so "the data, then its trend" reads as two beats. seriesLead(2) keeps
      // it behind the rolling avg whether or not that series is present. z:3 keeps it on top.
      animationDelay: seriesLead(2),
      animationDuration: TIMING.trendSweepMs,
      z: 3,
    });
  }

  const fmt = (v: number) => (Number.isInteger(v) ? v.toLocaleString() : v.toFixed(1));

  return {
    ...ECHARTS_ANIM,
    // Extra right padding so the final date label (e.g. "2026-05") isn't clipped at the grid's right
    // edge. With boundaryGap:false the last category sits exactly on the boundary, so alignMaxLabel on
    // the axis anchors that label's right side inward while the padding gives it room.
    grid: { left: 4, right: 28, top: 36, bottom: 24, containLabel: true },
    legend: {
      top: 0,
      right: 0,
      icon: 'roundRect',
      itemWidth: 14,
      itemHeight: 8,
      textStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: tokens.surface.border } },
      // Two-line tooltip mirrors the histogram's / calendar's: a header line naming the bucket (the
      // date), then a bold measured value with its metric unit, one row per series present that day.
      formatter: (params: any) => {
        const rows = Array.isArray(params) ? params : [params];
        const header = rows[0]?.axisValue ?? '';
        const body = rows
          .filter((r: any) => r.value != null && (Array.isArray(r.value) ? r.value[1] != null : true))
          .map((r: any) => {
            const v = Array.isArray(r.value) ? Number(r.value[1]) : Number(r.value);
            return `${r.marker}${r.seriesName} <strong>${fmt(v)}</strong> ${unit}`;
          })
          .join('<br/>');
        return `${header}<br/>${body}`;
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      boundaryGap: false,
      // Anchor the first/last labels inward so neither the earliest nor latest date clips against the
      // grid edge in a narrow panel (boundaryGap:false puts both exactly on the boundary).
      axisLabel: { hideOverlap: true, alignMinLabel: 'left', alignMaxLabel: 'right' },
    },
    yAxis: {
      type: 'value',
      name: unit,
      nameTextStyle: { color: tokens.text.secondary, fontFamily: tokens.fontFamily, fontSize: 12 },
      scale: true,
    },
    series,
  };
}

// memoized so a streaming re-render doesn't rebuild the option -> setOption(notMerge) reflow (see Histogram).
export const TrendLine = React.memo(function TrendLine({ title, spec, data }: { title: string; spec: TrendLineSpec; data: TrendLineData }) {
  const option = React.useMemo(() => buildTrendLineOption(spec, data), [spec, data]);
  return (
    <ChartCard title={title} downloadable>
      <ResponsiveECharts theme="studysync" option={option} style={{ height: 280 }} notMerge />
    </ChartCard>
  );
});
